import type { AttackWave, SimEvent, SimMetrics, World } from "./types.js";

export function computeMetrics(
  world: World,
  waves: AttackWave[],
  events: SimEvent[],
): SimMetrics {
  const initialTargetValue = world.targets.reduce((s, t) => s + t.value, 0);
  const remainingTargetValue = world.targets
    .filter((t) => !t.destroyed)
    .reduce((s, t) => s + t.value, 0);
  const defendedValue = remainingTargetValue;

  let attackerCost = 0;
  let totalWeapons = 0;
  let detectedWeapons = 0;
  let warningSum = 0;
  for (const wave of waves) {
    for (const w of wave.weapons) {
      attackerCost += w.cost;
      totalWeapons += 1;
      if (w.detectedAt !== null && w.terminalAt !== null) {
        detectedWeapons += 1;
        warningSum += Math.max(0, w.terminalAt - w.detectedAt);
      }
    }
  }

  const defenderCost =
    world.fixedDefences.reduce((s, f) => s + f.cost, 0) +
    world.mobileDefences.reduce((s, m) => s + m.cost, 0);

  const impactEvents = events.filter((e) => e.type === "impact");
  const targetDestroyedEvents = events.filter((e) => e.type === "targetDestroyed");
  const targetsHit = new Set(impactEvents.map((e) => (e as { targetId: string }).targetId)).size;
  const targetsDestroyed = targetDestroyedEvents.length;
  const leakers = impactEvents.length;

  const fixedDefenceShotsFired = world.fixedDefences.reduce((s, f) => s + f.shotsFired, 0);
  const mobileDefenceShotsFired = world.mobileDefences.reduce((s, m) => s + m.shotsFired, 0);
  const maxFixedShots = world.fixedDefences.reduce((s, f) => s + (f.shotsFired + f.magazine), 0);
  const maxMobileShots = world.mobileDefences.reduce(
    (s, m) => s + (m.shotsFired + m.magazine),
    0,
  );
  const fixedDefenceUtilisation = maxFixedShots > 0 ? fixedDefenceShotsFired / maxFixedShots : 0;
  const mobileDefenceUtilisation = maxMobileShots > 0 ? mobileDefenceShotsFired / maxMobileShots : 0;

  const warningTimeMean = detectedWeapons > 0 ? warningSum / detectedWeapons : 0;

  const attackedTargetIds = new Set<string>();
  for (const wave of waves) for (const w of wave.weapons) attackedTargetIds.add(w.targetId);
  const attackedTargets = world.targets.filter((t) => attackedTargetIds.has(t.id));
  const attackedValueTotal = attackedTargets.reduce((s, t) => s + t.value, 0);
  const attackedValuePreserved = attackedTargets
    .filter((t) => !t.destroyed)
    .reduce((s, t) => s + t.value, 0);
  const concentrationEfficiency =
    attackedValueTotal > 0 ? attackedValuePreserved / attackedValueTotal : 0;

  return {
    initialTargetValue,
    remainingTargetValue,
    defendedValue,
    attackerCost,
    defenderCost,
    leakers,
    targetsHit,
    targetsDestroyed,
    fixedDefenceShotsFired,
    mobileDefenceShotsFired,
    fixedDefenceUtilisation,
    mobileDefenceUtilisation,
    warningTimeMean,
    concentrationEfficiency,
  };
}
